import { useEffect, useMemo, useState } from 'react';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import type { UsuarioItem } from '@/shared/api/controllers/usuarios';
import { UsuarioDialog } from './UsuarioDialog';
import { UsuarioViewDialog } from './UsuarioViewDialog';
import { DeleteUsuarioDialog } from './DeleteUsuarioDialog';
import { useAllUsuarios } from './queries';
import type { EntityViewProps } from '../../types';

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; usuario: UsuarioItem }
  | { kind: 'view'; usuario: UsuarioItem }
  | { kind: 'delete'; usuario: UsuarioItem };

export function UsuariosEntity({
  search,
  filterValue,
  secondaryFilterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps) {
  const { data: usuarios = [], isPending, isFetching, error, refetch } = useAllUsuarios();
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  // ConfiguracionPage signals "Nuevo" button clicks via `openCreate`.
  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = [...usuarios].sort((a, b) =>
      (a.fields.UsuarioApp_USR ?? '').localeCompare(b.fields.UsuarioApp_USR ?? '', 'es'),
    );
    if (filterValue) {
      rows = rows.filter((u) => u.fields.Perfil_USR === filterValue);
    }
    if (secondaryFilterValue) {
      rows = rows.filter((u) => u.fields.Status_USR === secondaryFilterValue);
    }
    if (!q) return rows;
    return rows.filter((u) => {
      const usuario = (u.fields.UsuarioApp_USR ?? '').toLowerCase();
      const nombre = (u.fields.Nombre_USR ?? '').toLowerCase();
      const perfil = (u.fields.Perfil_USR ?? '').toLowerCase();
      return usuario.includes(q) || nombre.includes(q) || perfil.includes(q);
    });
  }, [usuarios, search, filterValue, secondaryFilterValue]);

  const columns: CrudColumn<UsuarioItem>[] = [
    {
      header: 'Status',
      width: 'w-32',
      render: (u) => <StatusBadge status={u.fields.Status_USR} />,
    },
    {
      header: 'Usuario',
      render: (u) => (
        <span className="font-medium text-foreground">{u.fields.UsuarioApp_USR || '—'}</span>
      ),
    },
    {
      header: 'Perfil',
      render: (u) => <span className="text-muted-foreground">{u.fields.Perfil_USR || '—'}</span>,
    },
    {
      header: 'Países',
      width: 'w-28',
      render: (u) => (
        <span className="text-muted-foreground">
          {formatPaisesPermitidos(u.fields.Paises_Permitidos_USR)}
        </span>
      ),
    },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'No hay usuarios cargados.'}
        onEdit={(u) => setMode({ kind: 'edit', usuario: u })}
        onView={(u) => setMode({ kind: 'view', usuario: u })}
        onDelete={(u) => setMode({ kind: 'delete', usuario: u })}
        isBackgroundFetching={isFetching}
      />

      <UsuarioDialog
        open={mode.kind === 'create' || mode.kind === 'edit'}
        onOpenChange={(o) => !o && closeDialog()}
        usuario={mode.kind === 'edit' ? mode.usuario : null}
        existingUsuarios={usuarios}
        onSuccess={(m) => onStatus({ kind: 'success', message: m })}
        onError={(m) => onStatus({ kind: 'error', message: m })}
      />
      <UsuarioViewDialog
        open={mode.kind === 'view'}
        onOpenChange={(o) => !o && closeDialog()}
        usuario={mode.kind === 'view' ? mode.usuario : null}
      />
      <DeleteUsuarioDialog
        open={mode.kind === 'delete'}
        onOpenChange={(o) => !o && closeDialog()}
        usuario={mode.kind === 'delete' ? mode.usuario : null}
        onSuccess={(m) => onStatus({ kind: 'success', message: m })}
        onError={(m) => onStatus({ kind: 'error', message: m })}
      />
    </>
  );
}

const PAIS_SHORT_LABEL: Record<'AR' | 'MX', string> = {
  AR: 'Arg',
  MX: 'Mex',
};

// AR before MX so the cell reads "Arg - Mex" rather than the order
// returned by SharePoint (which varies by how the row was last edited).
function formatPaisesPermitidos(codes: ('AR' | 'MX')[] | null | undefined): string {
  if (!codes || codes.length === 0) return '—';
  const ordered: Array<'AR' | 'MX'> = [];
  if (codes.includes('AR')) ordered.push('AR');
  if (codes.includes('MX')) ordered.push('MX');
  return ordered.map((c) => PAIS_SHORT_LABEL[c]).join(' - ');
}
